<template>
  <div class="index-list-view">
    <ftu-block
      dismiss-key="indexes"
      icon="assessment"
      title="These are indexes"
      description="The indexes below track the aggregate daily value movements of cards across various categories. The CL50 Index is like the S&P 500, made up of cards that were hand selected by our team. Use these indexes to research specific markets within the hobby to gauge where the markets are moving."
    />

    <page-header title="Indexes" />

    <align
      class="header"
      flex
      collapse-end
      direction="flex-start"
    >
      <div class="search-container">
        <search-area
          v-model="query"
          class="input"
        />
      </div>

      <align small-gap>
        <icon-button
          v-if="allowMoreCustomIndexes"
          icon="dashboard_customize"
          tooltip="Setup Custom Index"
          @click="showCustomIndexModal = true"
        />

        <direction-sort-options
          :sort.sync="sort"
          :direction.sync="direction"
          :options="sortOptions"
        />
      </align>
    </align>

    <div v-if="indexSnapshot">
      <div class="grid">
        <index-category-card
          v-for="index in filteredIndexes"
          :key="index.key"
          :as="index.as"
          :to="index.to"
          :text="index.label"
          :icon="index.icon"
          :highlight="index.custom"
          :percent-change="index.percentChange"
        />
      </div>
    </div>

    <portal to="fullscreen">
      <custom-index-setup-modal
        :open="showCustomIndexModal"
        @close="showCustomIndexModal = false"
        @updated="load"
      />
    </portal>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { INDEXES_SORT_OPTIONS, CUSTOM_INDEX_LIMIT } from '@cardladder/shared/constants';
import IndexCategoryCard from '@cardladder/shared/components/IndexCategoryCard';
import DirectionSortOptions from '@cardladder/shared/components/DirectionSortOptions';

import CustomIndexSetupModal from './CustomIndexSetupModal';

import { authService } from '@/services';

export default {
  components: {
    CustomIndexSetupModal,
    DirectionSortOptions,
    IndexCategoryCard,
  },

  data() {
    return {
      customIndexes: [],
      loaded: false,
      showCustomIndexModal: false,
      sortOptions: INDEXES_SORT_OPTIONS,
    };
  },

  mounted() {
    this.load();
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      indexSnapshot: (state) => state.indexSnapshot,
    }),

    allowMoreCustomIndexes() {
      return this.loaded && this.customIndexes.length < CUSTOM_INDEX_LIMIT;
    },

    indexes() {
      const indexes = [];

      this.customIndexes.forEach((customIndex) => {
        indexes.push({
          key: customIndex.index,
          label: customIndex.dailyIndex ? customIndex.label : 'Creating Index...',
          icon: 'assessment',
          as: customIndex.dailyIndex ? 'router-link' : 'div',
          to: `/indexes/custom/${customIndex.index}`,
          percentChange: customIndex ? customIndex[this.sort] || null : null,
          custom: true,
        });
      });

      if (this.indexSnapshot) {
        Object.keys(this.indexSnapshot).forEach((key) => {
          indexes.push({
            key,
            label: this.indexSnapshot[key].label,
            icon: this.indexSnapshot[key].icon,
            as: 'router-link',
            to: `/indexes/${key}`,
            percentChange: this.indexSnapshot[key][this.sort],
          });
        });
      }

      indexes.sort((a, b) => {
        if (this.direction === 'asc') {
          return a.percentChange - b.percentChange;
        }

        return b.percentChange - a.percentChange;
      });

      return indexes;
    },

    filteredIndexes() {
      return this.indexes.filter((index) => {
        if (!this.query) {
          return true;
        }

        return index.label.toLowerCase().includes(this.query.toLowerCase());
      });
    },

    sort: {
      get() {
        return this.$store.state.indexesSort;
      },

      set(value) {
        this.$store.commit('SET_INDEXES_SORT', value);
      },
    },

    direction: {
      get() {
        return this.$store.state.indexesDirection;
      },

      set(value) {
        this.$store.commit('SET_INDEXES_DIRECTION', value);
      },
    },

    query: {
      get() {
        return this.$store.state.indexQuery;
      },

      set(value) {
        this.$store.commit('SET_INDEX_QUERY', value);
      },
    },
  },

  methods: {
    async load() {
      this.customIndexes = await authService.getCustomIndexes(this.user.uid);
      this.loaded = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.index-list-view {
  @include container;
  min-height: 100%;

  h4 {
    margin: 0;
  }
}

.grid {
  @include grid-panel(3);
}

.header {
  margin-bottom: $gp;
}

.search-container {
  width: 100%;
}
</style>
